import { useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";
import { ArrowRight, Check, X } from "lucide-react";

import {
  getComponent,
  getComponentStatus,
  type ComponentLink,
  type ComponentStageStatus,
} from "@/api/components";
import { CopyButton } from "@/components/CopyButton";
import { ImportedFieldsNotice } from "@/components/ImportedFieldsNotice";
import { RefreshFailed, loadFailure } from "@/components/Spinner";
import { ButtonLink, EmptyState, Icon, Page, PageHeader, Table, TONE_INSET, type TableColumn } from "@/components/ui";

/**
 * Libellés lisibles des cinq maillons. Une clé absente d'ici (l'API en
 * ajoute un demain) s'affiche sous sa forme brute plutôt que d'être masquée
 * — voir ``LINK_LABEL[link] ?? link`` plus bas.
 */
const LINK_LABEL: Record<string, string> = {
  declared: "Declared",
  repository: "Repository",
  image: "Image",
  grants: "Grants",
  distributed: "Distributed",
};

/**
 * Le contenu d'une cellule = un maillon. Trois états, jamais deux : vert et
 * silencieux quand tout va bien ; gris quand le maillon est sans objet pour cette
 * brique (un widget frontend n'a ni dépôt, ni image, ni grants) ; sinon la cause
 * et, quand l'API en fournit un, le remède sous une forme copiable — c'est tout
 * l'intérêt de cette page : un opérateur pressé ne doit jamais avoir à aller
 * chercher la commande ailleurs. Un maillon en échec teinte sa cellule (voir
 * ``tint`` plus bas) : c'est le tableau qui pose le fond, pas ce contenu.
 *
 * L'ordre des tests compte : ``ok`` d'abord, pour qu'un maillon rouge reste
 * rouge quoi qu'il arrive plutôt que d'être masqué en gris.
 */
function LinkCell({ link }: { link: ComponentLink | undefined }) {
  if (!link) return <span className="text-ink-muted">—</span>;
  if (link.ok && !link.applicable) {
    return (
      <span className="text-ink-muted" title={link.detail}>
        <span aria-hidden="true">n/a</span>
        <span className="sr-only">Not applicable — {link.detail}</span>
      </span>
    );
  }
  if (link.ok) {
    return (
      <span className="text-success-fg" title={link.detail}>
        <Icon icon={Check} />
        <span className="sr-only">OK — {link.detail}</span>
      </span>
    );
  }
  return (
    <div className="text-warning-fg">
      <p className="flex items-start gap-1.5 font-medium">
        <Icon icon={X} className="mt-0.5" />
        <span>{link.detail}</span>
      </p>
      {link.remedy && (
        <div className="mt-2 flex items-start gap-2">
          <pre
            className={`min-w-0 flex-1 overflow-x-auto whitespace-pre-wrap break-words rounded border px-2 py-1 font-mono text-xs ${TONE_INSET.warning}`}
          >
            {link.remedy}
          </pre>
          <CopyButton value={link.remedy} />
        </div>
      )}
    </div>
  );
}

/** The stage's link of that name, if it has one. */
const linkOf = (stage: ComponentStageStatus, name: string) => stage.links.find((l) => l.link === name);

/** A link that failed: its cell is the one that asks to be read. */
const isFailing = (link: ComponentLink | undefined) => link !== undefined && !link.ok;

export function CatalogComponentStatusPage() {
  const { group = "", name = "" } = useParams<{ group?: string; name?: string }>();
  const auth = useAuth();
  const token = auth.user?.id_token ?? "";

  const query = useQuery({
    queryKey: ["component-status", group, name],
    queryFn: () => getComponentStatus(token, group, name),
    enabled: Boolean(token && group && name),
  });

  // Séparé de ``query`` (livrabilité) : ``source``/``pendingImport`` vivent
  // sur la brique elle-même, pas sur sa vue de statut. Même clé de query que
  // la page d'édition (``CatalogComponentEdit``) : si l'opérateur vient de
  // là, ou y va ensuite, le cache est déjà chaud plutôt que rechargé deux
  // fois pour la même donnée.
  const componentQuery = useQuery({
    queryKey: ["catalog", "component", group, name],
    queryFn: () => getComponent(token, group, name),
    enabled: Boolean(token && group && name),
  });

  // Les colonnes sont les maillons de TOUS les stages, dans l'ordre où on les rencontre : ceux du
  // premier stage d'abord (ils sont toujours rendus dans le même ordre, et cet ordre porte la
  // causalité — un maillon ne peut pas être vert si le précédent est rouge), puis ceux que d'autres
  // stages ajoutent. Une cellule est le maillon du stage PORTANT CE NOM : rendre par position
  // mettrait une valeur sous la mauvaise colonne dès qu'un stage a un autre ordre ou moins de
  // maillons, et perdrait ceux en trop. Un stage qui n'a pas le maillon montre un tiret.
  // Avant la réponse, et quand aucun stage ne réclame de compte, l'en-tête nomme quand même les
  // cinq maillons connus : le cadre ne change pas de forme à l'arrivée des données.
  const stages = query.data?.stages;
  const seen = (stages ?? []).flatMap((stage) => stage.links.map((l) => l.link));
  const links = seen.length > 0 ? [...new Set(seen)] : Object.keys(LINK_LABEL);

  const columns: TableColumn<ComponentStageStatus>[] = [
    {
      key: "deployment",
      header: "Deployment",
      cell: (stage) => (
        <>
          <div className="font-medium">
            {stage.tenant}/{stage.stage}
          </div>
          <div className={`mt-1 text-xs font-medium ${stage.deliverable ? "text-success-fg" : "text-warning-fg"}`}>
            {stage.deliverable ? "Deliverable" : "Not deliverable"}
          </div>
        </>
      ),
    },
    ...links.map(
      (link): TableColumn<ComponentStageStatus> => ({
        key: link,
        header: LINK_LABEL[link] ?? link,
        // 14rem: wide enough for a remedy to stay readable (the <pre> wraps),
        // narrow enough that the four publication links fit together in the
        // frame rather than pushing "Distributed" behind a scrollbar macOS does
        // not show. Only a column that has a failing cell needs the room.
        wide: (stages ?? []).some((stage) => isFailing(linkOf(stage, link))),
        tint: (stage) => (isFailing(linkOf(stage, link)) ? "warning" : undefined),
        cell: (stage) => <LinkCell link={linkOf(stage, link)} />,
      }),
    ),
  ];

  return (
    <Page>
      <PageHeader
        back={{ to: "/catalog/components", label: "Components" }}
        title={<span className="font-mono">{query.data?.componentRef ?? `${group}/${name}`}</span>}
      />

      {componentQuery.data?.pendingImport && (
        <div className="mb-6">
          <ImportedFieldsNotice
            source={componentQuery.data.source}
            pendingImport={componentQuery.data.pendingImport}
            pendingAction={
              <ButtonLink
                to={`/catalog/components/${group}/${name}/edit`}
                variant="primary"
              >
                Review & accept
                <Icon icon={ArrowRight} />
              </ButtonLink>
            }
          />
        </div>
      )}

      <RefreshFailed query={query} className="mb-4" />
      <Table
        label="Deployments"
        columns={columns}
        rows={stages}
        rowKey={(stage) => `${stage.tenant}/${stage.stage}`}
        error={loadFailure(query)}
        rowEmphasis={(stage) => (stage.deliverable ? undefined : "warning")}
        empty={
          <EmptyState title="No stage claims an AWS account">
            There is nothing to deliver to yet.
          </EmptyState>
        }
      />
    </Page>
  );
}
